import { create } from "zustand";
import { fetchLogin } from "./Services/fetchlogin";
import { fetchMenuAdmin } from "./Services/Menu/fetchMenu";
import { fetchCreateSeccion, fetchUpdateSeccion, fetchDeleteSeccion } from "./Services/Menu/fetchSecciones";
import { fetchCreateProducto, fetchUpdateProducto, fetchDeleteProducto } from "./Services/Menu/fetchProductos";
import { fetchCreatePromo, fetchUpdatePromo, fetchDeletePromo } from "./Services/Promos/fetchPromos";

// Si el backend responde 401 (token vencido o inválido) se cierra la sesión
// y el AuthGuard vuelve a mostrar el login.
const cerrarSesionSiExpiro = (response) => {
  if (response?.statusCode === 401) {
    localStorage.removeItem("logindata");
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("userId");
    window.dispatchEvent(new Event("authChange"));
  }
};

const porOrden = (a, b) => (a.orden ?? 0) - (b.orden ?? 0);

// Reemplaza (o agrega) un item dentro de una lista por su llave
const upsert = (lista, item, key) => {
  const existe = lista.some((x) => x[key] === item[key]);
  const nueva = existe ? lista.map((x) => (x[key] === item[key] ? item : x)) : [...lista, item];
  return nueva.sort(porOrden);
};

const useStore = create((set, get) => ({
  loading: false,
  error: null,
  success: null,

  resetError: () => set({ error: null }),
  resetSuccess: () => set({ success: null }),

  // --- Login ---
  fetchDataLogin: async (body) => {
    set({ loading: true, error: null, success: null });
    try {
      const response = await fetchLogin(body);

      if (response.statusCode !== 200) {
        set({ loading: false, error: response.message || "Error en login", success: null });
        return null;
      }

      // 💾 Persistir credenciales
      try {
        if (response?.token) localStorage.setItem("token", response.token);
        if (response?.role) localStorage.setItem("role", response.role);
        if (response?.userId) localStorage.setItem("userId", response.userId);
      } catch (_) { }

      set({ loading: false, success: response });
      return response;
    } catch (error) {
      set({ error: error.message || "Error en login", loading: false, success: null });
      return null;
    }
  },

  // --- Menú (panel) ---
  secciones: [],
  productos: [],
  promos: [],
  loadingMenuAdmin: false,
  errorMenuAdmin: null,
  guardando: false,

  fetchDataMenuAdmin: async () => {
    set({ loadingMenuAdmin: true, errorMenuAdmin: null });
    try {
      const response = await fetchMenuAdmin();
      cerrarSesionSiExpiro(response);

      if (response.statusCode !== 200) {
        set({ loadingMenuAdmin: false, errorMenuAdmin: response.message });
        return null;
      }

      set({
        loadingMenuAdmin: false,
        secciones: (response.secciones || []).sort(porOrden),
        productos: (response.productos || []).sort(porOrden),
        promos: (response.promos || []).sort(porOrden),
      });
      return response;
    } catch (error) {
      set({ loadingMenuAdmin: false, errorMenuAdmin: error.message || "No se pudo cargar el menú" });
      return null;
    }
  },

  // Todas las acciones de guardado regresan { ok, message } para que el
  // componente muestre el toast y cierre el modal.
  _guardar: async (peticion, alTerminar) => {
    set({ guardando: true });
    try {
      const response = await peticion();
      cerrarSesionSiExpiro(response);

      if (response.statusCode < 200 || response.statusCode >= 300) {
        set({ guardando: false });
        return { ok: false, message: response.message || "No se pudo guardar" };
      }

      alTerminar(response);
      set({ guardando: false });
      return { ok: true, message: response.message, item: response.item };
    } catch (error) {
      set({ guardando: false });
      return { ok: false, message: error.message || "No se pudo guardar" };
    }
  },

  // --- Secciones ---
  guardarSeccion: (body, seccionId) =>
    get()._guardar(
      () => (seccionId ? fetchUpdateSeccion(seccionId, body) : fetchCreateSeccion(body)),
      ({ item }) => set({ secciones: upsert(get().secciones, item, "seccionId") })
    ),

  eliminarSeccion: (seccionId) =>
    get()._guardar(
      () => fetchDeleteSeccion(seccionId),
      () => set({ secciones: get().secciones.filter((s) => s.seccionId !== seccionId) })
    ),

  // --- Productos ---
  guardarProducto: (body, productoId) =>
    get()._guardar(
      () => (productoId ? fetchUpdateProducto(productoId, body) : fetchCreateProducto(body)),
      ({ item }) => set({ productos: upsert(get().productos, item, "productoId") })
    ),

  eliminarProducto: (productoId) =>
    get()._guardar(
      () => fetchDeleteProducto(productoId),
      () => set({ productos: get().productos.filter((p) => p.productoId !== productoId) })
    ),

  // --- Promos ---
  guardarPromo: (body, promoId) =>
    get()._guardar(
      () => (promoId ? fetchUpdatePromo(promoId, body) : fetchCreatePromo(body)),
      ({ item }) => set({ promos: upsert(get().promos, item, "promoId") })
    ),

  eliminarPromo: (promoId) =>
    get()._guardar(
      () => fetchDeletePromo(promoId),
      () => set({ promos: get().promos.filter((p) => p.promoId !== promoId) })
    ),

  // --- Agregar aquí nuevas acciones ---
  // (una acción por feature, todas siguiendo el mismo patrón try/catch con `set`)
}));

export default useStore;
